import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import daeulLogo from "@/assets/Daeul_logo_eng_v2_cropped.png";
import AdminButton from "../common/AdminButton";
import { clearSession, fetchAdminCustomerInquiries, fetchAdminLifeSignalSummary, fetchDashboardSummary } from "@/lib/api";
import { adminNavigationGroups, type AdminBadgeKey } from "./adminNavigation";

type BadgeCounts = Record<AdminBadgeKey, number>;

interface AdminShellSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onCloseMobile: () => void;
  onToggle: () => void;
}

const createDefaultCollapsedGroups = () =>
  adminNavigationGroups.reduce<Record<string, boolean>>((acc, group) => {
    acc[group.section] = false;
    return acc;
  }, {});

const emptyBadgeCounts: BadgeCounts = {
  emergency: 0,
  lifeSignal: 0,
  workbox: 0,
  sound: 0,
  customerInquiry: 0,
};

const AdminShellSidebar = ({ collapsed, mobileOpen, onCloseMobile, onToggle }: AdminShellSidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(createDefaultCollapsedGroups);
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>(emptyBadgeCounts);

  useEffect(() => {
    let isMounted = true;

    const loadBadgeCounts = async () => {
      try {
        const [dashboardSummary, lifeSignalSummary, customerInquiries] = await Promise.all([
          fetchDashboardSummary(),
          fetchAdminLifeSignalSummary(),
          fetchAdminCustomerInquiries(),
        ]);

        if (!isMounted) return;

        const emergency =
          dashboardSummary.openEmergencyEventCount +
          dashboardSummary.acknowledgedEmergencyEventCount +
          dashboardSummary.inProgressEmergencyEventCount;
        const lifeSignal = lifeSignalSummary.totalNeedsAttentionCount;
        const sound = dashboardSummary.todayRiskSoundCount;
        const customerInquiry = customerInquiries.filter((item) => item.status === "RECEIVED" || item.status === "IN_PROGRESS").length;

        setBadgeCounts({
          emergency,
          lifeSignal,
          sound,
          customerInquiry,
          workbox: emergency + lifeSignal + sound + customerInquiry,
        });
      } catch {
        if (isMounted) {
          setBadgeCounts(emptyBadgeCounts);
        }
      }
    };

    void loadBadgeCounts();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const activeGroup = adminNavigationGroups.find((group) =>
      group.items.some((item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)),
    );

    if (!activeGroup) return;

    setCollapsedGroups((current) => {
      if (!current[activeGroup.section]) return current;
      return { ...current, [activeGroup.section]: false };
    });
  }, [location.pathname]);

  const navigationGroups = useMemo(
    () =>
      adminNavigationGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          badge: item.badgeKey ? badgeCounts[item.badgeKey] : undefined,
        })),
      })),
    [badgeCounts],
  );

  const toggleGroup = (section: string) => {
    setCollapsedGroups((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const handleLogout = () => {
    clearSession();
    onCloseMobile();
    navigate("/admin/login");
  };

  const handleNavigate = (path: string) => {
    onCloseMobile();
    navigate(path);
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-40 flex h-full flex-col bg-slate-950 transition-all duration-300 ${
        collapsed ? "w-[4.5rem]" : "w-[16.5rem]"
      } ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      style={{
        borderRight: "0.0625rem solid #1e293b",
      }}
    >
      <div className="flex items-center gap-3 border-b px-4 py-5" style={{ borderColor: "#1e293b" }}>
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
          style={{ background: "linear-gradient(135deg, #2563EB 0%, #0EA5E9 100%)" }}
        >
          <i className="ri-heart-pulse-line text-lg text-white" />
        </div>
        {!collapsed && (
          <div className="min-w-0 overflow-hidden">
            <img src={daeulLogo} alt="Da:eul" className="h-6 w-auto object-contain" />
            <span className="text-xs text-slate-400">최종본 관리자 콘솔</span>
          </div>
        )}
        <AdminButton variant="plain" size="auto"
          type="button"
          aria-label="관리자 메뉴 닫기"
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white lg:hidden"
          onClick={onCloseMobile}
        >
          <i className="ri-close-line text-lg" />
        </AdminButton>
      </div>

      {!collapsed && (
        <div className="mx-3 mt-3 rounded-2xl border border-blue-400/20 bg-blue-500/10 p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-200">통합 업무함</span>
            <span className="rounded-full bg-blue-400 px-2 py-0.5 text-xs font-bold text-slate-950">{badgeCounts.workbox}</span>
          </div>
          <p className="mt-1 text-xs leading-4 text-slate-400">긴급, 생활신호, 소리, 문의를 한 큐로 봅니다.</p>
        </div>
      )}

      <nav className="admin-scrollbar-hidden flex-1 overflow-y-auto py-2">
        {navigationGroups.map((group) => (
          <div key={group.section} className="mb-1">
            {!collapsed && (
              <AdminButton variant="plain" size="auto"
                type="button"
                onClick={() => toggleGroup(group.section)}
                className="flex w-full items-center justify-between px-4 pb-1.5 pt-3 text-left text-slate-500 transition-colors hover:text-slate-300"
                aria-expanded={!collapsedGroups[group.section]}
              >
                <span className="text-xs font-semibold uppercase tracking-wider">{group.section}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${collapsedGroups[group.section] ? "-rotate-90" : "rotate-0"}`} />
              </AdminButton>
            )}

            {(!collapsedGroups[group.section] || collapsed) &&
              group.items.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
                return (
                  <AdminButton variant="plain" size="auto"
                    key={`${group.section}-${item.path}`}
                    type="button"
                    onClick={() => handleNavigate(item.path)}
                    className={`relative flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      isActive ? "bg-blue-600/15 text-blue-300" : "text-slate-400 hover:bg-slate-700/40 hover:text-slate-100"
                    }`}
                    title={collapsed ? `${group.section} · ${item.label}` : undefined}
                  >
                    {isActive && <span className="absolute bottom-1.5 left-0 top-1.5 w-0.5 rounded-r bg-blue-400" />}
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/5">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    {!collapsed && (
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{item.label}</span>
                        <span className="block truncate text-xs text-slate-500">{item.description}</span>
                      </span>
                    )}
                    {item.badge !== undefined && item.badge > 0 && (
                      <span
                        className={`${
                          collapsed ? "absolute right-3 top-2 h-2.5 w-2.5" : "min-w-5 px-1.5 py-0.5"
                        } rounded-full bg-red-500 text-center text-xs font-bold leading-none text-white`}
                      >
                        {!collapsed ? item.badge : ""}
                      </span>
                    )}
                  </AdminButton>
                );
              })}
          </div>
        ))}
      </nav>

      <div className="border-t py-2" style={{ borderColor: "#1e293b" }}>
        <AdminButton variant="plain" size="auto"
          type="button"
          onClick={onToggle}
          className="hidden w-full items-center gap-3 px-4 py-2 text-slate-400 transition-colors hover:bg-slate-700/40 hover:text-slate-100 lg:flex"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center">
            <i className={collapsed ? "ri-arrow-right-s-line text-base" : "ri-arrow-left-s-line text-base"} />
          </span>
          {!collapsed && <span className="text-sm">메뉴 접기</span>}
        </AdminButton>
        <AdminButton variant="plain" size="auto"
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 px-4 py-2 text-slate-400 transition-colors hover:bg-slate-700/40 hover:text-red-300"
        >
          <span className="flex h-7 w-7 shrink-0 items-center justify-center">
            <i className="ri-logout-box-r-line text-base" />
          </span>
          {!collapsed && <span className="text-sm">로그아웃</span>}
        </AdminButton>
      </div>
    </aside>
  );
};

export default AdminShellSidebar;
